// Tenantra Inc. - Add Property: building type, address and auto-numbered units
import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Home, Building2, Save, ShieldCheck, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';
import { useLoadScript } from '@react-google-maps/api';
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete';
import { ApartmentUnits, HouseUnits, MAX_UNITS, rangeText } from '@/components/landlord/UnitsBuilder';

const GOOGLE_MAPS_LIBRARIES = ['places'];

// Canadian provinces
const PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

// Province name → code lookup
const PROVINCE_CODE_MAP = {};
PROVINCES.forEach(p => {
  PROVINCE_CODE_MAP[p.name.toLowerCase()] = p.code;
  PROVINCE_CODE_MAP[p.code.toLowerCase()] = p.code;
});

// Parse Google Place result into address fields
function parseAddressComponents(components) {
  const result = { street: '', city: '', province: '', postal_code: '' };
  let streetNumber = '';
  let route = '';

  for (const c of components) {
    const types = c.types;
    if (types.includes('street_number')) streetNumber = c.long_name;
    if (types.includes('route')) route = c.long_name;
    if (types.includes('locality') || types.includes('sublocality_level_1')) result.city = c.long_name;
    if (types.includes('administrative_area_level_1')) {
      const code = PROVINCE_CODE_MAP[c.long_name.toLowerCase()] || PROVINCE_CODE_MAP[c.short_name.toLowerCase()];
      if (code) result.province = code;
    }
    if (types.includes('postal_code')) result.postal_code = c.long_name;
  }

  result.street = streetNumber ? `${streetNumber} ${route}` : route;
  return result;
}

// Address autocomplete input
function AddressAutocompleteInput({ value, onChange, onSelect, error }) {
  const {
    ready,
    suggestions: { status, data },
    setValue: setAutocompleteValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: { componentRestrictions: { country: 'ca' } },
    debounce: 250,
  });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    setAutocompleteValue(val); // true by default - fetch suggestions
    setShowSuggestions(true);
  };

  const handleSelect = async (suggestion) => {
    setShowSuggestions(false);
    clearSuggestions();
    onChange(suggestion.structured_formatting.main_text);

    try {
      const details = await getDetails({ placeId: suggestion.place_id, fields: ['address_components'] });
      if (details?.address_components) {
        onSelect(parseAddressComponents(details.address_components));
      }
    } catch (err) {
      console.error('Failed to get place details:', err);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        id="street"
        placeholder="Start typing the street address"
        value={value}
        onChange={handleInput}
        onFocus={() => { if (data.length > 0) setShowSuggestions(true); }}
        disabled={!ready}
        autoComplete="off"
        className={`h-10 text-sm bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 ${
          error ? 'border-red-400' : ''
        }`}
        data-testid="street-address-input"
      />
      {showSuggestions && status === 'OK' && data.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
          data-testid="address-suggestions"
        >
          {data.map((suggestion) => (
            <li
              key={suggestion.place_id}
              onClick={() => handleSelect(suggestion)}
              className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
              data-testid={`address-suggestion-${suggestion.place_id}`}
            >
              <span className="font-medium">{suggestion.structured_formatting.main_text}</span>
              <span className="text-slate-400 text-xs ml-1">{suggestion.structured_formatting.secondary_text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const TYPES = [
  { value: 'apartment', label: 'Apartment building', sub: 'Several units at one address', icon: Building2 },
  { value: 'house', label: 'House', sub: 'One home, with optional suites', icon: Home },
];
const field = 'h-10 text-sm bg-white border-slate-200 text-slate-800 placeholder:text-slate-400';

const Step = ({ n, title, sub, children, testId }) => (
  <section className="bg-white border border-slate-200 rounded-lg" data-testid={testId}>
    <div className="px-5 pt-4 pb-3 flex items-start gap-3">
      <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</span>
      <div><h2 className="text-sm font-semibold text-slate-900">{title}</h2>{sub && <p className="text-xs text-slate-500">{sub}</p>}</div>
    </div>
    <div className="px-5 pb-5 pl-14">{children}</div>
  </section>
);

function TypePicker({ value, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {TYPES.map(({ value: v, label, sub, icon: Icon }) => {
        const on = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} className={`text-left flex items-start gap-3 p-4 rounded-lg border transition-colors ${on ? 'border-teal-600 bg-teal-50/60 ring-1 ring-teal-600' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-testid={`property-type-${v}`}>
            <Icon className={`w-5 h-5 mt-0.5 ${on ? 'text-teal-700' : 'text-slate-400'}`} />
            <span><span className="block text-sm font-medium text-slate-900">{label}</span><span className="block text-xs text-slate-500">{sub}</span></span>
          </button>
        );
      })}
    </div>
  );
}

function AddressFields({ isLoaded, formData, errors, onChange, onSelect }) {
  const err = (k) => errors[k] && <p className="text-red-500 text-xs mt-1">{errors[k]}</p>;
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="street" className="text-xs text-slate-600 mb-1 block">Street address</Label>
        {isLoaded
          ? <AddressAutocompleteInput value={formData.street} onChange={(v) => onChange('street', v)} onSelect={onSelect} error={errors.street} />
          : <Input id="street" value={formData.street} onChange={(e) => onChange('street', e.target.value)} className={`${field} ${errors.street ? 'border-red-400' : ''}`} data-testid="street-address-input" />}
        {err('street')}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label htmlFor="city" className="text-xs text-slate-600 mb-1 block">City</Label>
          <Input id="city" value={formData.city} onChange={(e) => onChange('city', e.target.value)} className={`${field} ${errors.city ? 'border-red-400' : ''}`} data-testid="city-input" />
          {err('city')}
        </div>
        <div>
          <Label htmlFor="province" className="text-xs text-slate-600 mb-1 block">Province</Label>
          <div className="relative">
            <select id="province" value={formData.province} onChange={(e) => onChange('province', e.target.value)} className="w-full h-10 px-3 pr-8 text-sm bg-white border border-slate-200 rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none" data-testid="province-select">
              {PROVINCES.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          </div>
        </div>
        <div>
          <Label htmlFor="postal" className="text-xs text-slate-600 mb-1 block">Postal code</Label>
          <Input id="postal" value={formData.postal_code} onChange={(e) => onChange('postal_code', e.target.value.toUpperCase())} placeholder="R1A 2B3" className={`${field} ${errors.postal_code ? 'border-red-400' : ''}`} data-testid="postal-code-input" />
          {err('postal_code')}
        </div>
      </div>
    </div>
  );
}

function LimitReached({ billingInfo }) {
  return (
    <div className="max-w-md mx-auto pt-10" data-testid="property-limit-reached">
      <a href="/landlord/properties" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-3.5 h-3.5" /> Back to Properties</a>
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
        <h2 className="text-base font-semibold text-slate-800 mb-1">Property Limit Reached</h2>
        <p className="text-sm text-slate-500 mb-5">Your <span className="font-medium capitalize">{billingInfo?.plan || ''}</span> plan allows up to <span className="font-medium">{billingInfo?.property_limit || 0}</span> properties. You're using <span className="font-medium">{billingInfo?.properties_used || 0}</span>.</p>
        <a href="/landlord/billing"><Button className="h-9 px-5 text-sm bg-teal-600 hover:bg-teal-700 text-white border-0">Upgrade Plan</Button></a>
      </div>
    </div>
  );
}

export default function AddPropertyPage() {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [limitReached, setLimitReached] = useState(false);
  const [billingInfo, setBillingInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ property_type: 'apartment', street: '', city: '', province: 'ON', postal_code: '' });
  const [apt, setApt] = useState({ count: '', start: '1', units: [] });
  const [houseUnits, setHouseUnits] = useState(['Main']);

  useEffect(() => {
    api.get('/billing/current-plan').then(res => { setBillingInfo(res.data); if (!res.data.can_add_property) setLimitReached(true); })
      .catch(() => {}).finally(() => setPageLoading(false));
  }, []);

  const isHouse = formData.property_type === 'house';
  const units = isHouse ? houseUnits : apt.units;
  const handleChange = (k, v) => { setFormData(p => ({ ...p, [k]: v })); if (errors[k]) setErrors(p => ({ ...p, [k]: null })); };
  const handleAddressSelect = (parsed) => {
    setFormData(p => ({ ...p, street: parsed.street || p.street, city: parsed.city || p.city, province: parsed.province || p.province, postal_code: parsed.postal_code || p.postal_code }));
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!formData.street || formData.street.length < 5) e.street = 'Street address must be at least 5 characters';
    if (!formData.city || formData.city.length < 2) e.city = 'City is required';
    if (!formData.postal_code) e.postal_code = 'Postal code is required';
    else if (!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(formData.postal_code)) e.postal_code = 'Invalid postal code (e.g. R1A 2B3)';
    if (!isHouse && apt.units.length === 0) e.units = 'Enter how many units and the first unit number';
    if (units.length > MAX_UNITS || (!isHouse && (parseInt(apt.count, 10) || 0) > MAX_UNITS)) e.units = `A property can have at most ${MAX_UNITS} units`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Please fix the highlighted fields'); return; }
    try {
      setLoading(true);
      const { street, city, province, postal_code, property_type } = formData;
      const res = await api.post('/properties', { property_type, units, address: { street, city, province, postal_code, unit_number: null } });
      toast.success(`Property added with ${units.length} unit${units.length === 1 ? '' : 's'}`);
      navigate(`/landlord/properties/${res.data.id}/verify`);
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
      const map = {};
      apiHelpers.formatValidationErrors(err).forEach(x => { if (x.field) map[x.field] = x.message; });
      setErrors(map);
    } finally { setLoading(false); }
  };

  if (pageLoading) return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  if (limitReached) return <LimitReached billingInfo={billingInfo} />;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="add-property-page">
      <div>
        <a href="/landlord/properties" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> Back to Properties</a>
        <h1 className="text-xl font-semibold text-slate-900">Add a property</h1>
        <p className="text-sm text-slate-500">Add the building once. Its units are created for you, ready to assign when you onboard a tenant.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Step n={1} title="What kind of property is it?" testId="step-type"><TypePicker value={formData.property_type} onChange={(v) => handleChange('property_type', v)} /></Step>
        <Step n={2} title="Where is it?" testId="step-address"><AddressFields isLoaded={isLoaded} formData={formData} errors={errors} onChange={handleChange} onSelect={handleAddressSelect} /></Step>
        <Step n={3} title={isHouse ? 'Rentable parts of the house' : 'Units in this building'} sub={isHouse ? null : 'Units are numbered in order from the first number, like a chequebook.'} testId="step-units">
          {isHouse ? <HouseUnits units={houseUnits} onChange={(v) => { setHouseUnits(v); if (errors.units) setErrors(p => ({ ...p, units: null })); }} /> : <ApartmentUnits {...apt} onChange={(v) => { setApt(v); if (errors.units) setErrors(p => ({ ...p, units: null })); }} />}
          {errors.units && <p className="text-red-500 text-xs mt-2" data-testid="units-error">{errors.units}</p>}
        </Step>
        <div className="flex items-start gap-2.5 px-1 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <p>Next, you'll upload one ownership document (property tax bill, title or mortgage statement). The whole building is verified once, not per unit.</p>
        </div>
        <div className="sticky bottom-0 -mx-1 px-1 py-3 bg-slate-50/90 backdrop-blur-sm flex items-center justify-between gap-3 border-t border-slate-200">
          <p className="text-sm text-slate-600" data-testid="add-property-summary">{units.length ? rangeText(units) : 'No units yet'}</p>
          <div className="flex gap-2">
            <a href="/landlord/properties"><Button type="button" variant="outline" className="h-9 px-4 text-sm">Cancel</Button></a>
            <Button type="submit" disabled={loading} className="h-9 px-5 text-sm bg-teal-600 hover:bg-teal-700 border-0" data-testid="add-property-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Add property</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
